/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React from "react";
import { quantityUnits, strings } from "../../../common/Strings/Strings";
import { CountryDetailsResponse } from "../../../models/management/CountryDetailsResponse";
import { TreatmentItemResponse } from "../../../models/treatment/TreatmentItemResponse";
import NumberFormatter from "../../../util/NumberFormatter";
import CostCalculator from "../../../util/CostCalculator";
import SimpleInput from "../../../components/InputFieldsSimple/SimpleInput";
import Button from "../../../components/Button";
import { XMark } from "../../../common/Icons/XMark";
import TreatmentCostCalculator from "../../../util/TreatmentCostCalculator";
import { ClinicResponse } from "../../../models/clinic/ClinicResponse";

interface Props {
  clinic?: ClinicResponse;
  changeQuantityOfItem?(treatmentItem: TreatmentItemResponse): void;
  countryDetails?: CountryDetailsResponse;
  deleteItem?(treatmentItem: TreatmentItemResponse): void;
  usedItems: TreatmentItemResponse[];
  showSum?: boolean;
}

const InventoryItemSection: React.FC<Props> = ({
  clinic,
  changeQuantityOfItem,
  countryDetails,
  usedItems,
  deleteItem,
  showSum = false,
}: Props) => {
  const val = (input: string): boolean =>
    !Number.isNaN(Number(input)) && input.length > 0 && Number(input) > 0;

  return (
    <div className="tw-table-container my-3" hidden={usedItems.length === 0}>
      <table className="tw-table">
        <thead className="bg-gray-50 dark:bg-gray-800 rounded-lg">
          <tr>
            <th className="py-2 px-4 text-sm font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400">
              {strings.name}
            </th>
            <th className="py-2 px-4 text-sm font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400">
              {strings.quantity}
            </th>
            <th className="py-2 px-4 text-sm font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400">
              {strings.acquisitionPrice} ({strings.gross})
            </th>
            <th className="py-2 px-4 text-sm font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400">
              {strings.salePrice} ({strings.gross})
            </th>
            <th />
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
          {usedItems.map((usedItem: TreatmentItemResponse) => (
            <tr key={usedItem.item.id}>
              <td className="px-4 py-2 text-sm font-medium">
                {usedItem.item.name}
              </td>
              <td className="px-4 py-2 text-sm font-medium whitespace-nowrap">
                {changeQuantityOfItem ? (
                  <SimpleInput
                    className="m-0"
                    name="quantity"
                    onChange={(newValue: string) => {
                      changeQuantityOfItem({
                        ...usedItem,
                        quantity: Number(newValue),
                      });
                    }}
                    suffix={
                      usedItem.item.acquisitionUnit &&
                      quantityUnits[usedItem.item.acquisitionUnit]
                    }
                    type="number"
                    validate={val}
                    value={usedItem.quantity.toString()}
                    width="80px"
                  />
                ) : (
                  <>
                    {usedItem.quantity}{" "}
                    {usedItem.item.acquisitionUnit &&
                      quantityUnits[usedItem.item.acquisitionUnit]}
                  </>
                )}
              </td>
              <td className="px-4 py-2 text-sm font-medium whitespace-nowrap">
                {NumberFormatter.formatPrice(
                  CostCalculator.getGrossPriceOfItem(
                    {
                      ...usedItem.item,
                      vat: usedItem.item.acquisitionVat,
                      price: usedItem.item.acquisitionPrice || 0,
                      quantity: usedItem.quantity,
                    },
                    countryDetails,
                    clinic
                  ),
                  usedItem.item.acquisitionCurrency
                )}
              </td>
              <td className="px-4 py-2 text-sm font-medium whitespace-nowrap">
                {NumberFormatter.formatPrice(
                  CostCalculator.getGrossPriceOfItem(
                    {
                      ...usedItem.item,
                      vat: usedItem.item.sellingVat,
                      price: usedItem.item.sellingPrice || 0,
                      quantity: usedItem.quantity,
                    },
                    countryDetails,
                    clinic
                  ),
                  usedItem.item.sellingCurrency
                )}
              </td>
              <td className="px-4 py-2 text-sm font-medium whitespace-nowrap text-right">
                {deleteItem ? (
                  <Button
                    type="button"
                    variant="basic"
                    onClick={() => deleteItem(usedItem)}
                  >
                    <XMark />
                  </Button>
                ) : (
                  <></>
                )}
              </td>
            </tr>
          ))}
          <tr hidden={!showSum}>
            <td className="px-4 py-2 text-sm font-bold whitespace-nowrap">
              {strings.sum}
            </td>
            <td />
            <td />
            <td className="px-4 py-2 text-sm font-bold whitespace-nowrap">
              {NumberFormatter.formatPrice(
                TreatmentCostCalculator.getGrossPriceOfItems2(
                  usedItems,
                  countryDetails,
                  clinic
                ),
                countryDetails?.currency
              )}
            </td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default InventoryItemSection;

InventoryItemSection.defaultProps = {
  changeQuantityOfItem: undefined,
  countryDetails: undefined,
  deleteItem: undefined,
};
